import { Injectable } from '@angular/core';
import { CredentialVaultService } from './credential-vault.service';
import { SessionClaimsService } from './session-claims.service';

/**
 * Identity for the mail-link weekly-report flow, which mints its own report-scoped JWT
 * from server-decrypted ids (`/token/generate-token-by-userid`) before any login.
 *
 * The token is kept under `sessionStorage.token`, encrypted at rest through the
 * credential vault like the app token, and is deliberately NEVER written to
 * `rt_session_token` (the app session key owned by `UserService`): keeping the keys
 * separate is what stops a report deep-link from becoming a full app session. The ids
 * are read from the token (`sub`, and `rt.cid` once the backend mints session claims)
 * instead of from the editable `userId` / `customerId` sessionStorage keys this flow
 * used to write. Design record: docs/superpowers/specs/2026-09-14-jwt-session-claims-design.md §3.3.
 */
@Injectable({ providedIn: 'root' })
export class WeeklyReportSessionService {
  private static readonly TOKEN_KEY = 'token';

  /**
   * @param sessionClaims - Pure JWT decoder shared with UserService.
   * @param vault - Encrypted sessionStorage vault the token is persisted through.
   */
  constructor(private sessionClaims: SessionClaimsService, private vault: CredentialVaultService) {}

  /**
   * Stores the report-scoped token.
   * @param token - Compact JWT from `generate-token-by-userid`.
   */
  setToken(token: string): void {
    this.vault.protect(WeeklyReportSessionService.TOKEN_KEY, token);
  }

  /**
   * @returns The stored report token, or `''` when none is stored.
   */
  token(): string {
    return this.vault.reveal(WeeklyReportSessionService.TOKEN_KEY);
  }

  /**
   * @returns The user id from the token's `sub` claim, or `0` when no token is stored.
   */
  userId(): number {
    return this.sessionClaims.subject(this.token())?.userId ?? 0;
  }

  /**
   * @returns The customer id from `rt.cid`, or `0` on a legacy (claims-less) token.
   */
  customerId(): number {
    return this.sessionClaims.from(this.token())?.customerId ?? 0;
  }

  /** Removes the report token. */
  clear(): void {
    this.vault.discard(WeeklyReportSessionService.TOKEN_KEY);
  }
}
